// Las acciones de un agente en pantalla (marco/acciones.ts decide CUÁLES; aquí, cómo se pintan y qué hacen). Una sola
// implementación para las tres superficies (05-11, auditoría final flujo.md #3, #4 y #10):
//   - `BotonAccionUrgente`: Revisar · Sí · Cerrar, en la fila de la lista y en la línea de encima de la caja del chat.
//   - `OpcionesAgente`: el menú del agente (la acción urgente, Abrir, Renombrar, Mover a ▸, Aparcar/Despertar, Terminar),
//     el mismo en la fila (clic derecho), en la mesa (clic derecho o dedo mantenido) y, sin Abrir ni Mover a, en el ⋮ del
//     chat. Sirve con las primitivas de Radix DropdownMenu o ContextMenu (tienen las mismas piezas).
import { listaRecienRecolocada } from './ordenQuieto';
import { Check, ChevronRight, DoorOpen, FolderInput, Handshake, KeyRound, LayoutGrid, MessageCircle, Moon, Pencil, Plus, Rocket, Sun, type LucideIcon } from 'lucide-react';
import { useState, type ComponentType, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useToastStore } from '../../../stores/toast.store';
import { oficinaApi } from '../api';
import { cerrarConDeshacer, useFaseCierre } from '../cierre/cerrarConDeshacer';
import type { ContratadoDTO } from '../compartido/vocabulario';
import type { Despliegue } from '../despliegues/api';
import { useBandejaStore } from '../despliegues/store';
import { mensajeError } from '../errores';
import { destinosDeSala } from '../mundo/grupos';
import type { MoverAGrupo } from '../mundo/MenuAgente';
import { ordenarConDeshacer } from '../mundo/ordenarConDeshacer';
import { useEsperasDeGrupos } from '../pelotas/porAgente';
import { useOficinaStore } from '../store';
import { Boton } from '../ui';
import { opcionesDelAgente, type AccionUrgente, type EstadoAgente } from './acciones';
import { quien } from './estado';
import { fraseDeFila } from './situacion';

// ─── La acción urgente ───

/** Dar permiso (el permiso sencillo del servidor). Lanza si falla: quien llama decide cómo lo enseña. */
const darPermiso = (c: ContratadoDTO, permisoId: string) => oficinaApi.responderPermiso(c.agentId, permisoId, 'aprobar');

/** Las acciones de la fila: cápsulas de 32 px; la zona táctil llega a 44 (::before). */
const CAPSULA = "relative h-8 min-h-0 min-w-11 px-3 text-o-sub before:absolute before:-inset-1.5 before:content-['']";

/**
 * El botón de la acción urgente. `donde`: en la fila (cápsula con zona táctil de 44) o en la línea del chat (botón
 * pequeño, como «Revisar la subida»). Oro para decidir (Revisar, Sí); gris para cerrar (el oro es para decidir).
 */
export function BotonAccionUrgente({ c, accion, donde }: { c: ContratadoDTO; accion: NonNullable<AccionUrgente<Despliegue>>; donde: 'fila' | 'chat' }) {
  if (accion.tipo === 'revisar') return <BotonRevisar c={c} subida={accion.subida} donde={donde} />;
  if (accion.tipo === 'si') return <SiRapido c={c} permisoId={accion.permisoId} donde={donde} />;
  return <BotonCerrar c={c} donde={donde} />;
}

const clasesDe = (donde: 'fila' | 'chat') => (donde === 'fila' ? { className: CAPSULA } : { tamano: 'sm' as const });

/** «Revisar»: abre la ventana de su subida (la misma que la de la bandeja, con su Allow). */
function BotonRevisar({ c, subida, donde }: { c: ContratadoDTO; subida: Despliegue; donde: 'fila' | 'chat' }) {
  const abrirSubida = useBandejaStore((s) => s.abrirSubida);
  return (
    <Boton variante="primario" {...clasesDe(donde)} onClick={() => (donde === 'fila' && listaRecienRecolocada() ? undefined : abrirSubida(subida.runId))} aria-label={t('oficinaV2.lista.accion.revisarAria', { nombre: quien(c), que: subida.paraPau.titulo })} data-testid={donde === 'fila' ? 'fila-revisar' : 'chat-revisar'}>
      {t('oficinaV2.lista.accion.revisar')}
    </Boton>
  );
}

/** «Cerrar»: sin diálogo, con 6 s para deshacer (cierre/cerrarConDeshacer.ts). */
function BotonCerrar({ c, donde }: { c: ContratadoDTO; donde: 'fila' | 'chat' }) {
  const fase = useFaseCierre(c.agentId);
  return (
    <Boton cargando={fase !== null} {...clasesDe(donde)} onClick={() => (donde === 'fila' && listaRecienRecolocada() ? undefined : cerrarConDeshacer([c]))} aria-label={t('oficinaV2.lista.accion.cerrarAria', { nombre: quien(c) })} data-testid={donde === 'fila' ? 'fila-cerrar' : 'chat-cerrar'}>
      {t('oficinaV2.lista.accion.cerrar')}
    </Boton>
  );
}

/**
 * «Sí»: aprueba el permiso sin abrir el chat (oro: es actuar). Mientras va, girando y quieto; si falla, un aviso con el
 * motivo. Al salir bien no hace falta nada más: el agente deja de pedir permiso y su fila se va de «Te toca» sola (llega
 * por el socket); hasta entonces el botón no vuelve, para no aprobar dos veces.
 */
export function SiRapido({ c, permisoId, donde = 'fila' }: { c: ContratadoDTO; permisoId: string; donde?: 'fila' | 'chat' }) {
  const [estado, setEstado] = useState<{ id: string; fase: 'enviando' | 'hecho' } | null>(null);
  const actual = estado?.id === permisoId ? estado.fase : null;
  if (actual === 'hecho') return null;
  const aprobar = async () => {
    setEstado({ id: permisoId, fase: 'enviando' });
    try {
      await darPermiso(c, permisoId);
      setEstado({ id: permisoId, fase: 'hecho' });
    } catch (e) {
      setEstado(null);
      useToastStore.getState().push({ message: t('oficinaV2.s12.rapido.error', { nombre: quien(c), motivo: mensajeError(e) }) });
    }
  };
  return (
    <Boton
      variante="primario"
      {...clasesDe(donde)}
      cargando={actual === 'enviando'}
      onClick={() => (donde === 'fila' && listaRecienRecolocada() ? undefined : void aprobar())}
      aria-label={t('oficinaV2.s12.rapido.aria', { nombre: quien(c), que: fraseDeFila(c) })}
      title={t('oficinaV2.s12.rapido.pista')}
      data-testid={donde === 'fila' ? 'si-rapido' : 'chat-si'}
    >
      {t('oficinaV2.s12.rapido.si')}
    </Boton>
  );
}

/** La acción urgente desde un menú (sin botón que gire: el aviso dice si falla). */
function ejecutarUrgente(c: ContratadoDTO, accion: NonNullable<AccionUrgente<Despliegue>>): void {
  if (accion.tipo === 'revisar') useBandejaStore.getState().abrirSubida(accion.subida.runId);
  else if (accion.tipo === 'cerrar') cerrarConDeshacer([c]);
  else
    darPermiso(c, accion.permisoId).catch((e: unknown) =>
      useToastStore.getState().push({ message: t('oficinaV2.s12.rapido.error', { nombre: quien(c), motivo: mensajeError(e) }) }),
    );
}

const URGENTE: Record<NonNullable<AccionUrgente>['tipo'], { icono: LucideIcon; texto: string }> = {
  revisar: { icono: Rocket, texto: 'oficinaV2.lista.subida.revisarLaSubida' },
  si: { icono: KeyRound, texto: 'oficinaV2.acciones.si' },
  cerrar: { icono: Check, texto: 'oficinaV2.acciones.cerrar' },
};

// ─── «Mover a ▸» fuera de la escena ───

/**
 * «Mover a ▸» de un agente con lo que hay en el almacén: los grupos de su sala, «Sin grupo», «Grupo nuevo…» (lo crea la
 * escena: `grupoNuevoPedido`) — lo mismo que el menú de su mesa, sin la elección de varias mesas (solo existe allí).
 */
export function useMoverA(c: ContratadoDTO): MoverAGrupo {
  const grupos = useOficinaStore((s) => s.grupos);
  const contratados = useOficinaStore((s) => s.contratados);
  const esperas = useEsperasDeGrupos();
  return {
    grupos: destinosDeSala(c.projectId, grupos, Object.values(contratados), (id) => !!esperas.get(id)?.conSanti),
    actual: c.grupoId ?? null,
    alMover: (grupoId) => {
      if (grupoId !== (c.grupoId ?? null)) ordenarConDeshacer(c.projectId, { accion: 'mover', agentId: c.agentId, grupoId });
    },
    alGrupoNuevo: () => useOficinaStore.getState().pedirGrupoNuevo(c.agentId),
  };
}

// ─── El menú ───

/** Las piezas de Radix que usa el menú: las de DropdownMenu y las de ContextMenu valen igual. */
export interface PrimitivasMenu {
  Item: ComponentType<{ onSelect?: (e: Event) => void; className?: string; children?: ReactNode }>;
  Separator: ComponentType<{ className?: string }>;
  Sub: ComponentType<{ children?: ReactNode }>;
  SubTrigger: ComponentType<{ className?: string; children?: ReactNode }>;
  SubContent: ComponentType<{ sideOffset?: number; collisionPadding?: number | Partial<Record<'top' | 'right' | 'bottom' | 'left', number>>; 'aria-label'?: string; className?: string; children?: ReactNode }>;
  Portal: ComponentType<{ children?: ReactNode }>;
}

export const OPCION_MENU =
  'flex min-h-11 cursor-pointer items-center gap-2.5 rounded-o-s px-3 py-2 text-o-cuerpo text-oficina-texto outline-none data-[highlighted]:bg-oficina-control data-[state=open]:bg-oficina-control';
export const CONTENIDO_MENU = 'z-[70] min-w-56 rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja animate-in fade-in-0 zoom-in-95';

export function OpcionMenu({ M, icono: Icono, atajo, extra, alElegir, testid, children }: { M: PrimitivasMenu; icono: LucideIcon | null; atajo?: string; extra?: ReactNode; alElegir: () => void; testid?: string; children: ReactNode }) {
  return (
    // ≥ 44 px de alto: se pulsa con el dedo.
    <M.Item onSelect={alElegir} className={OPCION_MENU} {...(testid ? { 'data-testid': testid } : {})}>
      {Icono ? <Icono className="h-4 w-4 shrink-0 text-oficina-texto2" aria-hidden /> : <span className="h-4 w-4 shrink-0" aria-hidden />}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {extra}
      {atajo ? <kbd className="shrink-0 font-sans text-o-nota text-oficina-texto3 [@media(pointer:coarse)]:hidden">{atajo}</kbd> : null}
    </M.Item>
  );
}

export interface PropsOpciones {
  M: PrimitivasMenu;
  c: ContratadoDTO;
  /** `aparcado`: duerme (el objeto de aparcar.ts o, si quien llama ya lo sabe por su marcador, un sí/no). */
  estado: Pick<EstadoAgente<Despliegue>, 'puedeAparcar' | 'accion'> & { aparcado: EstadoAgente['aparcado'] | boolean };
  /** Abrir su chat (la fila y la mesa; en el chat no). */
  alAbrir?: () => void;
  alRenombrar: () => void;
  alTerminar: () => void;
  /** «Mover a ▸» (la fila y la mesa) y «Mover de mesa» (abre «Ordenar»). */
  moverA?: MoverAGrupo;
  alMoverDeMesa?: () => void;
  /** La acción urgente va en el menú (fila y mesa); en el chat ya está en la línea de encima de la caja. */
  conUrgente?: boolean;
  /** Lo propio de cada sitio (p. ej. «Ver la información» en el chat), antes de «Terminar». */
  extra?: ReactNode;
  /** Enseñar «F2» junto a «Cambiar el nombre» (solo en la mesa, donde la tecla funciona). */
  atajoF2?: boolean;
}

/** El menú de un agente: las mismas opciones, en el mismo orden, en la fila, la mesa y el chat. */
export function OpcionesAgente({ M, c, estado, alAbrir, alRenombrar, alTerminar, moverA, alMoverDeMesa, conUrgente = true, extra, atajoF2 = false }: PropsOpciones) {
  const aparcar = (si: boolean) => void useOficinaStore.getState().aparcar(c.agentId, si);
  const urgente = conUrgente ? estado.accion : null;
  const opciones = opcionesDelAgente(c, estado, { abrir: !!alAbrir, moverA: !!moverA });
  // «Cerrar» (con deshacer) ya está arriba: «Terminar este trabajo» sería lo mismo dicho dos veces.
  const terminar = opciones.includes('terminar') && urgente?.tipo !== 'cerrar';
  return (
    <>
      {urgente ? (
        <>
          <OpcionMenu M={M} icono={URGENTE[urgente.tipo].icono} alElegir={() => ejecutarUrgente(c, urgente)} testid="menu-urgente">
            {t(URGENTE[urgente.tipo].texto as 'oficinaV2.acciones.si')}
          </OpcionMenu>
          <M.Separator className="my-1 h-px bg-oficina-linea" />
        </>
      ) : null}
      {opciones.includes('abrir') && alAbrir ? (
        <OpcionMenu M={M} icono={MessageCircle} alElegir={alAbrir}>
          {t('oficinaV2.escena.abrirChat')}
        </OpcionMenu>
      ) : null}
      {/* Las mismas palabras que el lápiz de la ficha. F2 solo funciona sobre la mesa: solo allí se enseña. */}
      <OpcionMenu M={M} icono={Pencil} {...(atajoF2 ? { atajo: 'F2' } : {})} alElegir={alRenombrar}>
        {t('oficinaV2.info.renombrar')}
      </OpcionMenu>
      {opciones.includes('moverA') && moverA ? <SubmenuMoverA M={M} moverA={moverA} alMoverDeMesa={alMoverDeMesa} /> : null}
      {opciones.includes('despertar') ? (
        <OpcionMenu M={M} icono={Sun} alElegir={() => aparcar(false)}>
          {t('oficinaV2.lista.aparcado.despertar')}
        </OpcionMenu>
      ) : opciones.includes('aparcar') ? (
        <OpcionMenu M={M} icono={Moon} alElegir={() => aparcar(true)}>
          {t('oficinaV2.lista.aparcado.aparcar')}
        </OpcionMenu>
      ) : null}
      {extra}
      {terminar ? (
        <>
          <M.Separator className="my-1 h-px bg-oficina-linea" />
          <OpcionMenu M={M} icono={DoorOpen} alElegir={alTerminar}>
            {t('oficinaV2.cierre.boton')}
          </OpcionMenu>
        </>
      ) : null}
    </>
  );
}

function SubmenuMoverA({ M, moverA, alMoverDeMesa }: { M: PrimitivasMenu; moverA: MoverAGrupo; alMoverDeMesa?: () => void }) {
  return (
    <M.Sub>
      <M.SubTrigger className={OPCION_MENU} {...{ 'data-testid': 'mover-a' }}>
        <FolderInput className="h-4 w-4 shrink-0 text-oficina-texto2" aria-hidden />
        <span className="flex-1">{t('oficinaV2.grupos.moverA')}</span>
        <ChevronRight className="h-4 w-4 shrink-0 text-oficina-texto3" aria-hidden />
      </M.SubTrigger>
      <M.Portal>
        <M.SubContent
          sideOffset={4}
          collisionPadding={8}
          aria-label={t('oficinaV2.grupos.moverA')}
          className={cn(CONTENIDO_MENU, 'z-[71] max-h-96 min-w-60 overflow-y-auto')}
        >
          {moverA.grupos.map((g) => (
            <OpcionMenu
              key={g.grupoId}
              M={M}
              icono={moverA.actual === g.grupoId ? Check : null}
              alElegir={() => moverA.alMover(g.grupoId)}
              extra={
                <span className="flex shrink-0 items-center gap-1 text-o-sub tabular-nums text-oficina-texto3">
                  {g.conSanti ? <Handshake className="h-3.5 w-3.5 text-oficina-santi" aria-label={t('oficinaV2.grupos.esperandoSanti')} /> : null}
                  {g.n ? g.n : t('oficinaV2.grupos.sugerido')}
                </span>
              }
            >
              {g.nombre}
            </OpcionMenu>
          ))}
          <OpcionMenu M={M} icono={moverA.actual === null ? Check : null} alElegir={() => moverA.alMover(null)}>
            {t('oficinaV2.grupos.sinGrupo')}
          </OpcionMenu>
          <M.Separator className="my-1 h-px bg-oficina-linea" />
          <OpcionMenu M={M} icono={Plus} atajo={t('oficinaV2.grupos.atajoAgrupar')} alElegir={moverA.alGrupoNuevo}>
            {t('oficinaV2.grupos.grupoNuevo')}
          </OpcionMenu>
          {alMoverDeMesa ? (
            <OpcionMenu M={M} icono={LayoutGrid} alElegir={alMoverDeMesa}>
              {t('oficinaV2.mesas.moverDeMesa')}
            </OpcionMenu>
          ) : null}
        </M.SubContent>
      </M.Portal>
    </M.Sub>
  );
}
